import { NA_VALUE } from "@/const/default-values"
import * as AdrDomain from "@/domain/adr"
import {
  DEFAULT_FORMAT,
  DEFAULT_LONG_FORMAT,
  getDate,
  getDateInUserLocale
} from "@/helpers/date.helpers"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { getDemoData, RULE_CATEGORY_AI } from "./demo"

import type {
  AdrIncidentResponse,
  AdrIncidentsResponse,
  EnvironmentsResponse,
  IncidentsHistoryResponse,
  RuleResponse
} from "@/api/useIncidents.types"
import type {
  AdrIncident,
  AIIncident,
  CVEIncident,
  CWEIncident,
  MitreIncident,
  Priority,
  PriorityDTO,
  RuleDTO,
  VulnerabilitiesMeta,
  WhatMitre,
  WhatMitreDTO
} from "@/domain/adr"
import type { LoadMoreMeta, LoadMoreResult } from "@/hooks/useHttp.types"

// PROT_* bitmask -> flag names. Add new bits here; humanizeProtFlags derives from it.
const PROT_FLAGS: readonly [number, string][] = [
  [0x1, "PROT_READ"],
  [0x2, "PROT_WRITE"],
  [0x4, "PROT_EXEC"]
]

// Duration unit -> milliseconds. Shared by parseDuration and formatDuration.
const MS = { d: 86_400_000, h: 3_600_000, m: 60_000, s: 1_000, ms: 1 } as const

export const getAdrIncidents = (
  response: AdrIncidentsResponse | AdrIncidentResponse
) => {
  const { data = [], ...rest } = response
  const arr = Array.isArray(data) ? data : [data]

  const parseDuration = (duration: string) => {
    const regex = /(\d+)(ms|d|h|m|s)/g

    let totalMs = 0

    for (const match of duration.matchAll(regex)) {
      totalMs += Number(match[1]) * MS[match[2] as keyof typeof MS]
    }

    return {
      milliseconds: totalMs,
      seconds: totalMs / MS.s,
      minutes: totalMs / MS.m,
      hours: totalMs / MS.h
    }
  }

  // The incidents API sends the raw PROT_* bitmask (e.g. 5); humanize it for display.
  const humanizeProtFlags = (flags?: number | null) => {
    if (flags == null) return undefined
    const names = PROT_FLAGS.filter(([bit]) => (flags & bit) !== 0).map(
      ([, name]) => name
    )
    return names.length ? names.join("|") : "PROT_NONE"
  }

  // Compact duration (e.g. "8h22m") from a millisecond span, for processAge.
  const formatDuration = (ms: number) => {
    if (!Number.isFinite(ms) || ms < 0) return undefined
    let rem = Math.floor(ms / MS.s) * MS.s // truncate to whole seconds
    const d = Math.floor(rem / MS.d)
    rem %= MS.d
    const h = Math.floor(rem / MS.h)
    rem %= MS.h
    const m = Math.floor(rem / MS.m)
    rem %= MS.m
    const s = Math.floor(rem / MS.s)
    const parts: string[] = []
    if (d) parts.push(`${d}d`)
    if (h) parts.push(`${h}h`)
    if (m) parts.push(`${m}m`)
    if (!d && !h && (s || !m)) parts.push(`${s}s`)
    return parts.join("") || "0s"
  }

  return {
    data: arr.map(({ incident: { details, general }, dismissDetails }) => {
      const { dismissedAt, dismissedBy } = dismissDetails || {}
      const isDismissed = !!dismissedAt || !!dismissedBy
      const { what, ...restDetails } = details
      const vulnerabilities = what?.vulnerabilities || []
      const hasVulnerabilities = !!vulnerabilities.length

      const vulnerabilitiesMeta = vulnerabilities.reduce(
        (acc: VulnerabilitiesMeta, value, index: number) => {
          const {
            packageVersions,
            cve,
            runtimeEvidence,
            vulnerableFunctions = []
          } = value

          if (!acc.packages[packageVersions]) {
            acc.packages[packageVersions] = []
            acc.packages.list.push({
              name: packageVersions,
              language: runtimeEvidence?.language || ("unknown" as Language)
            })
          }

          if (!acc.CVEs[cve]) {
            acc.CVEs[cve] = []
            acc.CVEs.list.push(cve)
          }

          acc.functions.list = [...acc.functions.list, ...vulnerableFunctions]
          ;(acc.packages[packageVersions] as number[]).push(index)
          ;(acc.CVEs[cve] as number[]).push(index)

          return acc
        },
        {
          CVEs: { list: [] },
          packages: { list: [] },
          functions: { list: [] },
          cve: hasVulnerabilities
            ? {
                detectedAt: getDateInUserLocale(
                  general.detectedAt,
                  DEFAULT_FORMAT
                ),
                cvePublishedDateUserLocale: getDateInUserLocale(
                  vulnerabilities[0].cvePublishedDate,
                  DEFAULT_FORMAT
                ),
                baselineDateUserLocale: getDateInUserLocale(
                  getDate(general.detectedAt).subtract(
                    vulnerabilities[0].daysSinceBaseline,
                    "day"
                  ),
                  DEFAULT_FORMAT
                ),
                vulnerableFunction: vulnerabilities[0].vulnerableFunctions?.[0],
                ...vulnerabilities[0]
              }
            : null
        }
      )

      const currentType = general.type
      const detectedAtUserLocale = general.detectedAt
        ? getDateInUserLocale(general.detectedAt, DEFAULT_LONG_FORMAT)
        : "[detected_at]"

      // Every incident type shares the same envelope; only `what` varies per
      // type. Build it once and let each case supply its (possibly transformed)
      // `what`, then cast to the concrete incident type.
      const toIncident = <W>(whatValue: W) => ({
        dismissDetails: isDismissed
          ? {
              dismissedTimestamp:
                dismissedAt && new Date(dismissedAt).getTime() / 1000,
              ...dismissDetails
            }
          : null,
        key: general.id,
        what: whatValue,
        ...general,
        ...restDetails,
        detectedAtUserLocale,
        priority: general.priority?.toLowerCase()
      })

      switch (currentType) {
        case "OS_COMMAND_INJECTION":
        case "CODE_INJECTION":
        case "UNSAFE_DESERIALIZATION":
          return toIncident(what) as CWEIncident

        case "DIRECTORY_TRAVERSAL":
        case "PTRACE_ATTACH":
        case "PTRACE_ANTI_DEBUG":
        case "UNEXPECTED_SHELL_SPAWNED":
        case "INTERACTIVE_SHELL_IN_CONTAINER":
        case "FILELESS_EXECUTION":
        case "INTERACTIVE_SESSION_SYSTEM_ACCOUNT":
        case "CREDENTIAL_HARVESTING_SEARCH":
        case "ANOMALOUS_SENSITIVE_FILE_READ":
        case "CODE_TAMPERING_LIBRARY_LOAD":
        case "CODE_TAMPERING_PROCESS_EXECUTION":
        case "CODE_TAMPERING_SCRIPT_LOAD":
        case "EXEC_MEMORY_NON_JIT":
        case "EXEC_MEMORY_ANOMALOUS_JIT": {
          const {
            containerStartTime,
            instanceStartTime,
            fileModifiedAt,
            processStartTime,
            modifiedFilePath,
            fileKind,
            parentExecutablePath,
            memorySyscall,
            memoryProtFlags,
            memoryAllocType,
            memoryProcessStartTime,
            vmBootTime
          } = what as WhatMitreDTO

          // Exec-memory rules carry the process start time in
          // memoryProcessStartTime; other rules use processStartTime.
          const effectiveProcessStartTime =
            processStartTime || memoryProcessStartTime
          // api-incidents sends the container start time as `instanceStartTime`
          // (proto instance_start_time); the UI rows/i18n read `containerStartTime`.
          const effectiveContainerStartTime =
            containerStartTime || instanceStartTime
          const isExecMemory = memorySyscall !== undefined

          return toIncident({
            containerStartTimeUserLocale: effectiveContainerStartTime
              ? getDateInUserLocale(
                  effectiveContainerStartTime,
                  DEFAULT_LONG_FORMAT
                )
              : NA_VALUE,
            fileModifiedAtUserLocale: fileModifiedAt
              ? getDateInUserLocale(fileModifiedAt, DEFAULT_LONG_FORMAT)
              : "[file_modified_at]",
            processStartTimeUserLocale: effectiveProcessStartTime
              ? getDateInUserLocale(
                  effectiveProcessStartTime,
                  DEFAULT_LONG_FORMAT
                )
              : "[process_start_time]",
            detectedAtMinusProcessAgeUserLocale: memoryProcessStartTime
              ? getDateInUserLocale(memoryProcessStartTime, DEFAULT_LONG_FORMAT)
              : general.detectedAt
                ? getDateInUserLocale(
                    getDate(general.detectedAt).subtract(
                      parseDuration((what as WhatMitre).processAge || "0s")
                        .milliseconds,
                      "ms"
                    ),
                    DEFAULT_LONG_FORMAT
                  )
                : NA_VALUE,
            vmBootTimeUserLocale: vmBootTime
              ? getDateInUserLocale(vmBootTime, DEFAULT_LONG_FORMAT)
              : "[vm_boot_time]",
            ...what,
            // KOD-7912: api-incidents uses different field names than the UI
            // rows + i18n read for code-tampering. Map them so the incidents
            // don't render "n/a" + raw {{vars}}. Conditional spreads: when the
            // api field is absent the spread `...what` value is kept, so other
            // rule types are unaffected.
            ...(modifiedFilePath !== undefined
              ? { filePath: modifiedFilePath }
              : {}),
            ...(fileKind !== undefined ? { scriptLanguage: fileKind } : {}),
            ...(parentExecutablePath !== undefined
              ? { parentProcessExecutablePath: parentExecutablePath }
              : {}),
            // KOD-7912: api-incidents delivers raw memory* fields; the UI rows
            // and i18n summaries read the humanized names. Map/humanize them
            // here so EXEC_MEMORY incidents don't render "n/a" + raw {{vars}}.
            ...(isExecMemory
              ? {
                  syscallName: memorySyscall,
                  allocType: memoryAllocType,
                  protFlagsHuman: humanizeProtFlags(memoryProtFlags),
                  processStartTime: memoryProcessStartTime
                    ? getDateInUserLocale(
                        memoryProcessStartTime,
                        DEFAULT_LONG_FORMAT
                      )
                    : (what as WhatMitreDTO).processStartTime,
                  processAge:
                    memoryProcessStartTime && general.detectedAt
                      ? formatDuration(
                          getDate(general.detectedAt).diff(
                            getDate(memoryProcessStartTime)
                          )
                        )
                      : (what as WhatMitre).processAge
                }
              : {})
          }) as MitreIncident
        }

        case "POTENTIAL_EXPLOIT_CVE":
        case "EXPOSURE_TO_NEWLY_PUBLISHED_CVE": {
          return toIncident({
            vulnerabilitiesMeta,
            package: vulnerabilities?.[0]?.runtimeEvidence?.package,
            ...what
          }) as CVEIncident
        }

        default:
          if (AdrDomain.isAiIncidentType(currentType))
            return toIncident(what) as AIIncident

          throw new Error(`Unknown incident type: ${currentType}`)
      }
    }),
    ...rest
  } as KosmosApi.Response<AdrIncident[]>
}

export const getListAdrIncidents = (
  response:
    | LoadMoreResult<
        AdrIncidentsResponse["data"],
        LoadMoreMeta & { priorityCounts?: Record<PriorityDTO, number> }
      >
    | undefined
) => {
  if (!response) return undefined

  const { metadata, pages = [] } = response

  if (!metadata) return undefined

  const { data, ...rest } = getAdrIncidents({
    data: pages.flatMap((page) => page),
    metadata
  })

  const idsByPriority = data.reduce(
    (acc, incident, index) => {
      const { priority } = incident

      if (!acc[priority]) acc[priority] = []

      acc[priority].push(index)

      return acc
    },
    {} as Partial<Record<Priority, number[]>>
  )

  const prioritiesByCount = Object.keys(
    metadata.priorityCounts || idsByPriority
  ).map((key) => key.toLowerCase() as Priority)

  const rawCounts = Object.entries(metadata.priorityCounts || {}).reduce(
    (acc, [key, value]) => {
      acc[key.toLowerCase() as Priority] = value
      return acc
    },
    {} as Partial<Record<Priority, number>>
  )

  const byPriority = prioritiesByCount.reduce(
    (acc, priority) => {
      const ids = idsByPriority[priority] ?? []

      acc[priority] = { ids, count: rawCounts[priority] ?? ids.length }

      return acc
    },
    {} as Record<Priority, { ids: number[]; count: number }>
  )

  return {
    data: {
      list: data,
      ...byPriority
    },
    ...rest
  }
}

export const getAdrIncidentsHistory = (
  response: IncidentsHistoryResponse,
  isDemo?: boolean
) => {
  if (!isDemo || !response?.data) {
    return response
  }

  const { data, ...rest } = response
  const demoIncidents = getDemoData()

  // Create incident details from demo incidents
  const demoIncidentDetails = demoIncidents.map(({ incident }) => ({
    id: incident.general.id,
    detectionTime: new Date(incident.general.createdAt)
  }))

  // Helper function to find the appropriate bucket for an incident
  const findBucketForIncident = (incidentTime: Date, buckets: any[]) => {
    // Sort buckets by startTime to ensure proper ordering
    const sortedBuckets = [...buckets].sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    )

    // Find the bucket that contains this incident time
    for (let i = 0; i < sortedBuckets.length; i++) {
      const bucket = sortedBuckets[i]
      const bucketStart = new Date(bucket.startTime)

      // For the last bucket, check if incident is after bucket start
      if (i === sortedBuckets.length - 1) {
        if (incidentTime >= bucketStart) {
          return bucket
        }
      } else {
        // For other buckets, check if incident is within bucket range
        const nextBucket = sortedBuckets[i + 1]
        const nextBucketStart = new Date(nextBucket.startTime)

        if (incidentTime >= bucketStart && incidentTime < nextBucketStart) {
          return bucket
        }
      }
    }

    // If no specific bucket found, add to the most recent (last) bucket
    return sortedBuckets[sortedBuckets.length - 1]
  }

  // Merge demo incidents into appropriate buckets
  const updatedBuckets = data.buckets.map((bucket) => {
    // Find demo incidents that belong to this specific bucket
    const demoIncidentsInBucket = demoIncidentDetails.filter((detail) => {
      const targetBucket = findBucketForIncident(
        detail.detectionTime,
        data.buckets
      )
      return (
        targetBucket &&
        new Date(targetBucket.startTime).getTime() ===
          new Date(bucket.startTime).getTime()
      )
    })

    return {
      ...bucket,
      count: bucket.count + demoIncidentsInBucket.length,
      incidentDetails: [...bucket.incidentDetails, ...demoIncidentsInBucket]
    }
  })

  return {
    ...rest,
    data: {
      ...data,
      buckets: updatedBuckets,
      openCount: demoIncidents.filter(
        ({ dismissDetails }) => !dismissDetails?.dismissedAt
      ).length,
      dismissedCount: demoIncidents.filter(
        ({ dismissDetails }) => !!dismissDetails?.dismissedAt
      ).length
    }
  }
}

export const getRulesForAdrIncidents = (response: RuleResponse) => {
  const { data = [], ...rest } = response
  const nextData = {
    RULE_CATEGORY_VULNERABILITY_EXPLOIT: {
      order: 5,
      ids: ["POTENTIAL_EXPLOIT_CVE"],
      alphaIds: []
    },
    RULE_CATEGORY_VULNERABILITY_EXPOSURE: {
      order: 6,
      ids: ["EXPOSURE_TO_NEWLY_PUBLISHED_CVE"],
      alphaIds: []
    },
    RULE_CATEGORY_DYNAMIC_CODE_EXECUTION: {
      order: 1,
      ids: ["CODE_INJECTION", "UNSAFE_DESERIALIZATION"],
      alphaIds: ["OS_COMMAND_INJECTION"]
    },
    RULE_CATEGORY_FILESYSTEM_ACTIVITY: {
      order: 3,
      ids: ["DIRECTORY_TRAVERSAL"],
      alphaIds: ["ANOMALOUS_SENSITIVE_FILE_READ"]
    },
    RULE_CATEGORY_PROCESS_EXECUTION: {
      order: 2,
      ids: [
        "PTRACE_ATTACH",
        "PTRACE_ANTI_DEBUG",
        "CREDENTIAL_HARVESTING_SEARCH",
        "INTERACTIVE_SESSION_SYSTEM_ACCOUNT",
        "FILELESS_EXECUTION"
      ],
      alphaIds: ["UNEXPECTED_SHELL_SPAWNED"]
    },
    RULE_CATEGORY_CONTAINER_K8S: {
      order: 4,
      ids: [],
      alphaIds: ["INTERACTIVE_SHELL_IN_CONTAINER"]
    },
    [RULE_CATEGORY_AI]: {
      order: 7,
      ids: [...AdrDomain.AI_INCIDENT_TYPES],
      alphaIds: []
    }
  } as Record<string, { ids: string[]; alphaIds: string[]; order: number }>

  return {
    data: data.reduce(
      (acc, rule) => {
        const { categoryId } = rule

        if (!nextData[categoryId]) return acc

        if (!acc.data[categoryId]) {
          acc.list.push({
            label: rule.category,
            value: categoryId,
            type: rule.reference?.type || ""
          })
          acc.data[categoryId] = {
            rules: { ids: [], alphaIds: [] },
            detectedIncidentsCount: 0
          }
          acc.list = acc.list.sort(
            (a, b) => nextData[a.value].order - nextData[b.value].order
          )
        }

        const idsByCategory = nextData[categoryId]
        const alphaIdsIndex = idsByCategory.alphaIds.findIndex(
          (id) => id === rule.id
        )

        if (alphaIdsIndex === -1) acc.data[categoryId].rules.ids.push(rule)
        else acc.data[categoryId].rules.alphaIds.push(rule)

        acc.data[categoryId].rules.ids = acc.data[categoryId].rules.ids.sort(
          (a, b) => a.name.localeCompare(b.name)
        )
        acc.data[categoryId].rules.alphaIds = acc.data[
          categoryId
        ].rules.alphaIds.sort((a, b) => a.name.localeCompare(b.name))

        acc.data[categoryId].detectedIncidentsCount +=
          rule.detectedIncidentsCount || 0

        return acc
      },
      { list: [], data: {} } as {
        list: { label: string; value: string; type: string }[]
        data: Record<
          string,
          {
            rules: {
              ids: RuleDTO[]
              alphaIds: RuleDTO[]
            }
            detectedIncidentsCount: number
          }
        >
      }
    ),
    ...rest
  }
}

export const getEnvironmentsForAdrIncidents = (
  response: EnvironmentsResponse
) => {
  const { data = [], ...rest } = response

  let allDisabled = true
  const countIncidents = data.reduce((acc, env) => {
    if (allDisabled && env.isMonitoringAdr) allDisabled = false

    return acc + (env.detectedIncidentsCount || 0)
  }, 0)

  const col = 50

  return {
    data: data
      .sort((a, b) => b.applications.length - a.applications.length)
      .map((environment, index) => {
        const isLastRow = index + col >= data.length
        const shouldApplyBR = index >= col && isLastRow
        const isStartOfLastRow = shouldApplyBR && index % col === 0
        const nextIndex = index + 1
        const isEndOfRowLastRow =
          shouldApplyBR && nextIndex === data.length && nextIndex % col === 0

        return {
          ...environment,
          id: `${environment.id}_${index}`,
          index,
          br: isStartOfLastRow,
          bl: isEndOfRowLastRow,
          label: capitalizeFirstLetter(environment.name),
          value: environment.name,
          incidentsCount: environment.detectedIncidentsCount || 0
        }
      }),

    allDisabled,
    countIncidents,
    hasIncidents: countIncidents > 0,
    ...rest
  }
}
